import { mdiAlertCircle, mdiPaperclip } from '@mdi/js';
import { Grow, Tooltip } from '@mui/material';
import { Formik, FormikHelpers as FormikActions, FormikProps, getIn, yupToFormErrors } from 'formik';
import _ from 'lodash';
import React, { useCallback, useContext, useRef, useState } from 'react';
import * as Yup from 'yup';
import { DoneButton, UndoneButton } from '..';
import { ProviderDoneButton } from '../provider-done-button';
import { ProviderUndoneButton } from '../provider-undone-button';
import { MessagePurposeType } from '../../../api';
import { MessagingContext } from '../../../services/messaging/context';
import { isSupport, TooltipTransitionComponent } from '../../base';
import * as S from './chat-controls.styles';

interface IChatComment {
    note: boolean;
    comment: string;
    attachments: File[];
}

interface IChatControlsProps {
    note?: boolean;
    hideNote?: boolean;
    hideDone?: boolean;
}

export const ChatControls: React.FunctionComponent<IChatControlsProps> = (
    props: IChatControlsProps,
): React.ReactElement => {
    const context = useContext(MessagingContext);

    const {
        isReadOnly,
        message,
        providerMessage,
        enableMessageTypeValidation,
        onComposeComment,
        onMarkAsClosed,
        onMarkAsOpen,
        onProviderMarkAsClosed,
        onProviderMarkAsOpen,
        onMessageClose,
    } = context;
    const initial: IChatComment = {
        note: !!props.note || !props.hideNote,
        comment: '',
        attachments: [],
    };
    const attachInput = useRef<HTMLInputElement | null>(null);
    const [rows, setRows] = useState(1);
    const [locked, setLocked] = useState(false);

    const handleDoneClick = async (): Promise<void> => {
        if (!locked && message && message.id) {
            setLocked(true);
            await onMarkAsClosed(message.id);
            setLocked(false);
            onMessageClose();
        }
    };
    const handleUndoneClick = async (): Promise<void> => {
        if (!locked && message && message.id) {
            setLocked(true);
            await onMarkAsOpen(message.id);
            setLocked(false);
            onMessageClose();
        }
    };
    const handleProviderDoneClick = async (): Promise<void> => {
        if (!locked && message && message.id && onProviderMarkAsClosed) {
            setLocked(true);
            await onProviderMarkAsClosed(message.id);
            setLocked(false);
            onMessageClose();
        }
    };
    const handleProviderUndoneClick = async (): Promise<void> => {
        if (!locked && message && message.id && onProviderMarkAsOpen) {
            setLocked(true);
            await onProviderMarkAsOpen(message.id);
            setLocked(false);
            onMessageClose();
        }
    };
    const handleAttachFile = (): void => {
        if (!!attachInput && attachInput.current) {
            attachInput.current.click && attachInput.current.click();
        }
    };
    const handleFileChange = (
        formik: FormikProps<IChatComment>,
    ): ((e: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (e: React.ChangeEvent<HTMLInputElement>): void => {
            const { attachments } = formik.values;
            const files = _.map(e.target.files, file => file);
            formik.setFieldValue('attachments', _.concat(attachments, files));
        };
    };
    const handleRemoveAttachment = (formik: FormikProps<IChatComment>, attachment: object): (() => void) => {
        return (): void => {
            const { attachments } = formik.values;
            formik.setFieldValue('attachments', _.without(attachments, attachment));
            // NB: force reset so file is removed
            if (attachInput && attachInput.current) {
                attachInput.current.value = '';
            }
        };
    };
    const handleChangeComment = (
        formik: FormikProps<IChatComment>,
    ): ((e: React.ChangeEvent<HTMLTextAreaElement>) => void) => {
        return (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
            const comment = e.target.value;
            formik.setFieldValue('comment', comment);
            const multiRow = _.size(comment) >= 40 || _.includes(comment, '\n');
            setRows(multiRow ? 8 : 1);
        };
    };

    const onChangeNote = (formik: FormikProps<IChatComment>): ((value: boolean) => void) => {
        return (value: boolean): void => {
            formik.setFieldValue('note', value);
        };
    };

    const handleKeydown = (formik: FormikProps<IChatComment>): ((e: React.KeyboardEvent) => void) => {
        return (e: React.KeyboardEvent): void => {
            // if (e.key === 'Enter' && e.shiftKey) {
            //     formik.submitForm();
            // }
        };
    };

    const handleBlur = (formik: FormikProps<IChatComment>) => {
        return (e: React.FocusEvent<HTMLTextAreaElement>) => {
            formik.setFieldTouched('comment', true);
        };
    };

    const handlePaste = (
        formik: FormikProps<IChatComment>,
    ): ((e: React.ClipboardEvent<HTMLTextAreaElement>) => void) => {
        return (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
            const images = _.map(e.clipboardData.items, item => {
                if (item.type.startsWith('image/')) {
                    return item.getAsFile();
                }
            });
            if (images && images.length) {
                const { attachments } = formik.values;
                const newAttachments = _.compact(_.concat(attachments, images));
                formik.setFieldValue('attachments', newAttachments);
            }
        };
    };

    const handleSubmit = async (
        values: IChatComment,
        { setFieldValue }: FormikActions<IChatComment>,
    ): Promise<void> => {
        if (message && message.id) {
            const { comment, attachments, note } = values;
            setLocked(true);
            try {
                await (onComposeComment &&
                    onComposeComment({ messageId: message.id, body: comment, note, files: attachments }));
                setRows(1);
                setFieldValue('comment', '');
                setFieldValue('attachments', []);
            } catch (err) {
                console.log(err);
                throw err;
            } finally {
                setLocked(false);
            }
        }
    };
    const handleSave = (formik: FormikProps<IChatComment>): (() => Promise<void>) => {
        // NB: avoid non-async formik.submitForm for now
        return async (): Promise<void> => {
            const errors = await formik.validateForm();
            if (_.isEmpty(errors)) {
                await handleSubmit(formik.values, formik);
                formik.resetForm();
            }
        };
    };

    const generateValidationSchema = useCallback(() => {
        const shape = {
            comment: Yup.string().when(
                ['$attachments'],
                (attachments: File[] | undefined, schema: any) => {
                    if ((attachments || []).length > 0) {
                        return schema;
                    }

                    return schema.required('Comment is required');
                },
            ),
            attachments: Yup.array(),
        };

        if (enableMessageTypeValidation) {
            if (message?.purposeType === MessagePurposeType.PROVIDERDOCUMENTCOLLECTION) {
                shape.attachments = shape.attachments.min(1, 'Document is required for this type of message');
            }

            if (message?.purposeType === MessagePurposeType.PROVIDERNUMBERCOLLECTION) {
                shape.comment = shape.comment.test(
                    'comment',
                    'You must specify a number for this type of message',
                    value => !isNaN(Number(value)),
                );
            }
        }

        return Yup.object().shape(shape);
    }, [message?.purposeType]);

    const getComposePlaceholder = useCallback(() => {
        let placeholder = 'Type your message here';
        if (enableMessageTypeValidation) {
            if (message?.purposeType === MessagePurposeType.PROVIDERNUMBERCOLLECTION) {
                placeholder = 'Pleace specify a number';
            }
        }

        return placeholder;
    }, [message?.purposeType]);

    const getInputAccept = useCallback(() => {
        if (enableMessageTypeValidation) {
            if (message?.purposeType === MessagePurposeType.PROVIDERDOCUMENTCOLLECTION) {
                return 'application/pdf,.pdf,.xls,.xlsx,.csv,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            }
        }

        return;
    }, [message?.purposeType]);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initial}
            onSubmit={handleSubmit}
            validate={(values: IChatComment) => {
                const schema = generateValidationSchema();
                try {
                    schema.validateSync(values, { abortEarly: false, context: values });

                    return {};
                } catch (err) {
                    return yupToFormErrors(err);
                }
            }}>
            {(formik: FormikProps<IChatComment>): React.ReactElement => {
                const attached = _.map(
                    formik.values.attachments,
                    (attachment: File, i: number): React.ReactElement => (
                        <S.StyledAttachmentButton
                            key={`attachment-${i}`}
                            filename={attachment.name}
                            filesize={attachment.size}
                            onRemove={handleRemoveAttachment(formik, attachment)}
                        />
                    ),
                );

                const { errors, touched: formTouched } = formik;

                const commentError = getIn(errors, 'comment');
                const attachmentsError = getIn(errors, 'attachments');
                const touched = getIn(formTouched, 'comment');

                const error = attachmentsError || commentError;

                const errorMessage = touched && error ? error : undefined;

                if (isReadOnly) {
                    return <></>;
                }

                const placeholder = getComposePlaceholder();

                return (
                    <S.Controls isNote={formik.values.note}>
                        {!props.hideNote && (
                            <Grow in={true}>
                                <S.NoteContainer>
                                    <S.NoteToggle
                                        disabled={locked}
                                        value={formik.values.note}
                                        onChange={onChangeNote(formik)}
                                    />
                                    {formik.values.note ? (
                                        <S.Note>Sending as internal note</S.Note>
                                    ) : (
                                        <S.Comment>Sending as provider message</S.Comment>
                                    )}
                                </S.NoteContainer>
                            </Grow>
                        )}
                        <S.InputWrapper>
                            <S.InputContainer>
                                <S.InputBox locked={locked}>
                                    <S.Col>
                                        {attached && attached.length ? (
                                            <S.InputAttachments>{attached}</S.InputAttachments>
                                        ) : null}
                                        <S.Textarea
                                            rows={rows}
                                            value={formik.values.comment}
                                            onChange={handleChangeComment(formik)}
                                            onKeyDown={handleKeydown(formik)}
                                            onPaste={handlePaste(formik)}
                                            onBlur={handleBlur(formik)}
                                            placeholder={placeholder}
                                            disabled={locked}
                                        />
                                    </S.Col>
                                </S.InputBox>
                                <S.ActionsWrapper>
                                    <S.ActionsCol>
                                        <S.ActionsItem>
                                            <S.SendButton
                                                variant="primary"
                                                onClick={handleSave(formik)}
                                                disabled={locked}>
                                                Send
                                            </S.SendButton>
                                        </S.ActionsItem>
                                        <S.ActionsItem>
                                            <S.AttachInput
                                                ref={attachInput}
                                                accept={isSupport ? getInputAccept() : 'application/pdf,.pdf,.xls,.xlsx,.csv,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
                                                type="file"
                                                onChange={handleFileChange(formik)}
                                            />
                                            <Tooltip
                                                title={<span>Upload file</span>}
                                                TransitionComponent={TooltipTransitionComponent}>
                                                <div>
                                                    <S.AttachButton
                                                        variant="outlined"
                                                        onClick={handleAttachFile}
                                                        disabled={locked}>
                                                        <S.AttachIcon path={mdiPaperclip} size={0.75} />
                                                    </S.AttachButton>
                                                </div>
                                            </Tooltip>
                                        </S.ActionsItem>
                                        <S.ActionsItem>
                                            {message && message.open && !props.hideDone && (
                                                <DoneButton onClick={handleDoneClick} disabled={locked} />
                                            )}
                                            {message && !message.open && (
                                                <UndoneButton onClick={handleUndoneClick} disabled={locked} />
                                            )}
                                        </S.ActionsItem>
                                        {isSupport && providerMessage && (
                                            <S.ActionsItem>
                                                {providerMessage.open && !props.hideDone && (
                                                    <ProviderDoneButton onClick={handleProviderDoneClick} disabled={locked} />
                                                )}
                                                {!providerMessage.open && (
                                                    <ProviderUndoneButton onClick={handleProviderUndoneClick} disabled={locked} />
                                                )}
                                            </S.ActionsItem>
                                        )}
                                    </S.ActionsCol>
                                </S.ActionsWrapper>
                                {!!touched && errorMessage && (
                                    <S.InputError>
                                        <S.InputErrorIcon path={mdiAlertCircle} />
                                        <S.InputErrorMessage>{errorMessage}</S.InputErrorMessage>
                                    </S.InputError>
                                )}
                            </S.InputContainer>
                        </S.InputWrapper>
                    </S.Controls>
                );
            }}
        </Formik>
    );
};
